import { useEffect, useRef, useState } from "react";
import type { OutputLine } from "@dramapie/shared";

import { OUTPUT_KIND_LABEL, sortOutputs } from "../../lib/outputs";

export interface EpisodeMoveMenuProps {
  open: boolean;
  /** 触发按钮（集列表那一行）：浮层按它的位置展开。 */
  anchor: HTMLElement | null;
  /** 「第 2 集 · 账房」：浮层标题里点名是哪一集。 */
  episodeLabel: string;
  /** 可移入的目标线（调用方已排除当前线与归档线）。 */
  targets: readonly OutputLine[];
  busy?: boolean;
  onSelect: (outputId: string) => void;
  onClose: () => void;
}

/**
 * 「把这一集移到别的产出线」浮层（M8 补充，契约 §3 的唯一入口）。
 *
 * 为什么放在集列表行而不是产出线浮层里：移动的对象是**某一集**，
 * 在集那一行点最直接（少一次「先选线再选集」的心智负担）。
 */
export function EpisodeMoveMenu({
  open,
  anchor,
  episodeLabel,
  targets,
  busy = false,
  onSelect,
  onClose,
}: EpisodeMoveMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number; width: number } | null>(null);

  useEffect(() => {
    if (!open || !anchor) {
      setPosition(null);
      return;
    }
    const measure = () => {
      const rect = anchor.getBoundingClientRect();
      const width = Math.max(rect.width, 220);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
      setPosition({ left, top: rect.bottom + 6, width });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }, 0);

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
      anchor?.focus();
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (ref.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;

  const items = sortOutputs(targets);

  return (
    <div
      ref={ref}
      className="dp-om dp-ep-move-menu"
      role="menu"
      aria-label="移到别的产出线"
      style={position ?? undefined}
      data-testid="episode-move-menu"
    >
      <div className="dp-om-head">
        <span>移到产出线</span>
        <span className="dp-om-head-note">{episodeLabel}</span>
      </div>
      <div className="dp-om-list">
        {items.length === 0 ? (
          <p className="dp-om-empty">没有可移入的产出线（归档的不能放集）</p>
        ) : null}
        {items.map((output) => (
          <button
            key={output.id}
            type="button"
            role="menuitem"
            className="dp-om-select dp-ep-move-target"
            disabled={busy}
            onClick={() => onSelect(output.id)}
          >
            <span className="dp-om-mark" aria-hidden="true">
              {output.title.slice(0, 1)}
            </span>
            <span className="dp-om-copy">
              <span className="dp-om-name">{output.title}</span>
              <span className="dp-om-meta">
                {output.aspect} · {OUTPUT_KIND_LABEL[output.kind]}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
